/**
 * AIAssistantSettingsPage — Enterprise-only rename setting for the AI assistant.
 *
 * Route: /settings/ai-assistant
 * Visibility: menu item hidden for non-Enterprise tenants.
 * API: PUT /api/v1/admin/tenant/ai-assistant-name { aiAssistantName: string }
 *
 * Design matches AURA settings conventions (SettingsPage.tsx patterns):
 *   - Border card layout, var(--bg-surface), design tokens throughout.
 *   - Light theme only (founder rule, Apr 17 2026).
 */
import { useState, useEffect } from 'react';
import { Save, Sparkles, Loader2, CheckCircle2, Crown } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { useTenantConfig } from '@/hooks/useTenantConfig';

export function AIAssistantSettingsPage() {
  const { aiAssistantName, isEnterprise } = useTenantConfig();
  const refreshTenant = useAuthStore((s) => s.refreshTenant);

  const [name, setName] = useState(aiAssistantName);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Sync when tenant config loads/changes (e.g. after page refresh)
  useEffect(() => {
    setName(aiAssistantName);
  }, [aiAssistantName]);

  // Non-Enterprise tenants see an upgrade prompt, not the form.
  if (!isEnterprise) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ background: 'linear-gradient(135deg, #C98A3B22, #B87A2E33)', border: '1px solid var(--saffron-300, #E8C07A)' }}
          >
            <Sparkles size={18} style={{ color: 'var(--saffron-600, #B87A2E)' }} />
          </div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--ink-0)', fontFamily: 'var(--ff-serif, serif)' }}>
            AI Assistant Settings
          </h1>
        </div>

        <div
          className="rounded-xl border p-6 text-center"
          style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}
        >
          <div
            className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
            style={{ background: 'var(--saffron-50, #FBF3DF)' }}
          >
            <Crown size={22} style={{ color: 'var(--saffron-600, #B87A2E)' }} />
          </div>
          <h2 className="mb-2 text-base font-semibold" style={{ color: 'var(--ink-0)' }}>
            Enterprise Feature
          </h2>
          <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
            Custom AI assistant naming is available on the Enterprise plan. Upgrade to rename
            your AI assistant and give it a brand identity unique to your organisation.
          </p>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name cannot be empty.');
      return;
    }
    if (trimmed.length > 30) {
      setError('Name must be 30 characters or fewer.');
      return;
    }
    setError('');
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/admin/tenant/ai-assistant-name', { aiAssistantName: trimmed });
      // Refresh tenant config in store so AIPanel + header update immediately.
      await refreshTenant();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const charCount = name.trim().length;
  const isValid = charCount >= 1 && charCount <= 30;

  return (
    <div className="mx-auto max-w-2xl">
      {/* Page header */}
      <div className="mb-6 flex items-center gap-3">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-lg"
          style={{
            background: 'linear-gradient(135deg, #C98A3B22, #B87A2E33)',
            border: '1px solid var(--saffron-300, #E8C07A)',
          }}
        >
          <Sparkles size={18} style={{ color: 'var(--saffron-600, #B87A2E)' }} />
        </div>
        <div>
          <h1
            className="text-xl font-bold"
            style={{ color: 'var(--ink-0)', fontFamily: 'var(--ff-serif, serif)' }}
          >
            AI Assistant Settings
          </h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--ink-3)' }}>
            Customise how your AI assistant is presented across AURA.
          </p>
        </div>
      </div>

      {/* Rename card */}
      <div
        className="rounded-xl border p-6"
        style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}
      >
        <h2 className="mb-1 text-sm font-semibold" style={{ color: 'var(--ink-0)' }}>
          AI Assistant Name
        </h2>
        <p className="mb-4 text-xs" style={{ color: 'var(--ink-3)' }}>
          This name appears in the AI panel header, chat placeholder, and the AI Insights page
          throughout AURA. Leave as "Kiaan" to keep the default.
        </p>

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label
              htmlFor="ai-assistant-name"
              className="mb-1 block text-xs font-medium"
              style={{ color: 'var(--ink-2)' }}
            >
              Display Name
            </label>
            <input
              id="ai-assistant-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
              maxLength={30}
              placeholder="Kiaan"
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors"
              style={{
                background: 'var(--ivory-0)',
                borderColor: error ? 'var(--red-500, #EF4444)' : 'var(--border-primary)',
                color: 'var(--ink-0)',
              }}
              onFocus={(e) => { if (!error) e.currentTarget.style.borderColor = 'var(--ink-1)'; }}
              onBlur={(e) => { if (!error) e.currentTarget.style.borderColor = 'var(--border-primary)'; }}
            />
            <div className="mt-1 flex items-center justify-between">
              {error ? (
                <span className="text-xs" style={{ color: 'var(--red-500, #EF4444)' }}>{error}</span>
              ) : (
                <span className="text-xs" style={{ color: 'var(--ink-3)' }}>
                  1–30 characters
                </span>
              )}
              <span
                className="text-xs tabular-nums"
                style={{ color: charCount > 30 ? 'var(--red-500, #EF4444)' : 'var(--ink-3)' }}
              >
                {charCount}/30
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isValid}
            className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity disabled:opacity-50"
            style={{
              background: 'var(--ink-0)',
              color: 'var(--ivory-0)',
              marginBottom: 22, // align with input bottom edge (offset by label + error row)
            }}
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : saved ? (
              <CheckCircle2 size={14} />
            ) : (
              <Save size={14} />
            )}
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save'}
          </button>
        </div>

        {/* Live preview */}
        <div
          className="mt-4 rounded-lg border px-3 py-2.5"
          style={{ background: 'var(--ivory-0)', borderColor: 'var(--line)' }}
        >
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--ink-3)' }}>
            Preview
          </div>
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                width: 22,
                height: 22,
                background: 'linear-gradient(135deg, #C98A3B, #B87A2E)',
                flexShrink: 0,
              }}
            >
              <Sparkles size={11} style={{ color: '#fff' }} />
            </div>
            <div>
              <div className="text-[12px] font-semibold" style={{ color: 'var(--ink-0)' }}>
                {name.trim() || 'Kiaan'}, your AI assistant
              </div>
              <div className="text-[10px]" style={{ color: 'var(--ink-3)' }}>
                How this appears in the AI panel header
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
